import type { ExportReport } from '@/types'

/**
 * Generate a pretty-printed JSON report.
 */
export function generateJSONReport(report: ExportReport): string {
  return JSON.stringify(report, null, 2)
}

/**
 * Escape special LaTeX characters in a string.
 */
function escapeLatex(str: string): string {
  return str
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/[&%$#_{}]/g, (m) => '\\' + m)
    .replace(/~/g, '\\textasciitilde{}')
    .replace(/\^/g, '\\textasciicircum{}')
}

/**
 * Format a number for LaTeX output.
 */
function formatNumber(n: number, decimals: number = 6): string {
  if (n < 0.001 && n > 0) {
    return n.toExponential(3)
  }
  return n.toFixed(decimals)
}

/**
 * Generate a LaTeX table from the report.
 */
export function generateLaTeXTable(report: ExportReport): string {
  const lines: string[] = []

  lines.push('\\begin{tabular}{llrrl}')
  lines.push('\\hline')
  lines.push('\\textbf{Test Name} & \\textbf{Statistic} & \\textbf{p-value} & \\textbf{Result} \\\\')
  lines.push('\\hline')

  // SP 800-22 results
  if (report.results.sp80022) {
    for (const test of report.results.sp80022.tests) {
      const name = escapeLatex(test.testName)
      const stat = formatNumber(test.statistic)
      const pVal = test.pValues.length > 0 ? formatNumber(test.pValues[0]) : '---'
      const result = test.overallPassed ? 'Pass' : 'Fail'
      lines.push(`${name} & ${stat} & ${pVal} & ${result} \\\\`)
    }
  }

  // SP 800-90B results
  if (report.results.sp80090b) {
    const r = report.results.sp80090b
    lines.push('\\hline')
    lines.push(`IID Conclusion & --- & --- & ${r.iidTestResults.iidConclusion ? 'IID' : 'Non-IID'} \\\\`)
    lines.push(`Min-Entropy & ${formatNumber(r.finalMinEntropy)} & --- & --- \\\\`)
  }

  // Tunable bias results
  if (report.results.tunableBias) {
    lines.push('\\hline')
    for (const test of report.results.tunableBias.tests) {
      const name = escapeLatex(test.testName)
      let stat = '---'
      let pVal = '---'
      let result = '---'

      if (test.testName === 'Bias Accuracy') {
        const t = test as import('@/types').BiasAccuracyResult
        stat = formatNumber(t.zStatistic)
        pVal = formatNumber(t.pValue)
        result = t.passed ? 'Pass' : 'Fail'
      } else if (test.testName === 'Bias Stability') {
        const t = test as import('@/types').BiasStabilityResult
        stat = formatNumber(t.homogeneity.chiSquare)
        pVal = formatNumber(t.homogeneity.pValue)
        result = t.homogeneity.passed ? 'Pass' : 'Fail'
      } else if (test.testName === 'Generalized Runs') {
        const t = test as import('@/types').GeneralizedRunsResult
        stat = formatNumber(t.onesRuns.chiSquare)
        pVal = formatNumber(t.onesRuns.pValue)
        result = t.onesRuns.passed ? 'Pass' : 'Fail'
      } else if (test.testName === 'Generalized Serial') {
        const t = test as import('@/types').GeneralizedSerialResult
        stat = formatNumber(t.chiSquare)
        pVal = formatNumber(t.pValue)
        result = t.passed ? 'Pass' : 'Fail'
      }

      lines.push(`${name} & ${stat} & ${pVal} & ${result} \\\\`)
    }
  }

  // Gaussian results
  if (report.results.gaussian) {
    lines.push('\\hline')
    for (const test of report.results.gaussian.distributionTests) {
      const name = escapeLatex(test.testName)
      let stat = '---'
      const pVal = formatNumber(test.pValue)
      const result = test.passed ? 'Pass' : 'Fail'

      if (test.testName === 'Shapiro-Wilk') {
        stat = formatNumber((test as import('@/types').ShapiroWilkResult).W)
      } else if (test.testName === 'Anderson-Darling') {
        stat = formatNumber((test as import('@/types').AndersonDarlingResult).A2)
      } else if (test.testName === "D'Agostino-Pearson") {
        stat = formatNumber((test as import('@/types').DAgostinoPearsonResult).K2)
      } else if (test.testName === 'Kolmogorov-Smirnov') {
        stat = formatNumber((test as import('@/types').KSTestResult).D)
      }

      lines.push(`${name} & ${stat} & ${pVal} & ${result} \\\\`)
    }
  }

  lines.push('\\hline')
  lines.push('\\end{tabular}')

  return lines.join('\n')
}

/**
 * Generate a full CSV report with all test results in one file.
 */
export function generateFullCSV(report: ExportReport): string {
  const lines: string[] = []
  lines.push('suite,testName,statistic,pValue,result')

  // SP 800-22 results
  if (report.results.sp80022) {
    for (const test of report.results.sp80022.tests) {
      const pVal = test.pValues.length > 0 ? test.pValues[0].toString() : ''
      const result = test.overallPassed ? 'Pass' : 'Fail'
      lines.push(`SP 800-22,"${test.testName}",${test.statistic},${pVal},${result}`)
    }
  }

  // SP 800-90B results
  if (report.results.sp80090b) {
    const r = report.results.sp80090b
    for (const stat of r.iidTestResults.statistics) {
      lines.push(`SP 800-90B IID,"${stat.name}",${stat.originalValue},${stat.rank}/${stat.totalPermutations},${stat.passed ? 'Pass' : 'Fail'}`)
    }
    for (const est of r.entropyEstimates) {
      lines.push(`SP 800-90B Entropy,"${est.estimatorName}",${est.minEntropy},,`)
    }
    lines.push(`SP 800-90B Summary,Final Min-Entropy,${r.finalMinEntropy},,${r.iidTestResults.iidConclusion ? 'IID' : 'Non-IID'}`)
  }

  // Tunable bias results
  if (report.results.tunableBias) {
    for (const test of report.results.tunableBias.tests) {
      let stat = ''
      let pVal = ''
      let result = ''

      if (test.testName === 'Bias Accuracy') {
        const t = test as import('@/types').BiasAccuracyResult
        stat = t.zStatistic.toString()
        pVal = t.pValue.toString()
        result = t.passed ? 'Pass' : 'Fail'
      } else if (test.testName === 'Bias Stability') {
        const t = test as import('@/types').BiasStabilityResult
        stat = t.homogeneity.chiSquare.toString()
        pVal = t.homogeneity.pValue.toString()
        result = (t.homogeneity.passed && t.trend.passed) ? 'Pass' : 'Fail'
      } else if (test.testName === 'Generalized Runs') {
        const t = test as import('@/types').GeneralizedRunsResult
        stat = t.onesRuns.chiSquare.toString()
        pVal = t.onesRuns.pValue.toString()
        result = (t.onesRuns.passed && t.zerosRuns.passed) ? 'Pass' : 'Fail'
      } else if (test.testName === 'Generalized Serial') {
        const t = test as import('@/types').GeneralizedSerialResult
        stat = t.chiSquare.toString()
        pVal = t.pValue.toString()
        result = t.passed ? 'Pass' : 'Fail'
      }

      lines.push(`Tunable-Bias,"${test.testName}",${stat},${pVal},${result}`)
    }
  }

  // Gaussian results
  if (report.results.gaussian) {
    for (const test of report.results.gaussian.distributionTests) {
      let stat = ''
      if (test.testName === 'Shapiro-Wilk') stat = (test as import('@/types').ShapiroWilkResult).W.toString()
      else if (test.testName === 'Anderson-Darling') stat = (test as import('@/types').AndersonDarlingResult).A2.toString()
      else if (test.testName === "D'Agostino-Pearson") stat = (test as import('@/types').DAgostinoPearsonResult).K2.toString()
      else if (test.testName === 'Kolmogorov-Smirnov') stat = (test as import('@/types').KSTestResult).D.toString()

      lines.push(`Gaussian,"${test.testName}",${stat},${test.pValue},${test.passed ? 'Pass' : 'Fail'}`)
    }
  }

  return lines.join('\n')
}

/**
 * Generate a self-contained Python script that loads the data and creates basic plots.
 */
export function generatePythonScript(report: ExportReport): string {
  const lines: string[] = []
  lines.push('#!/usr/bin/env python3')
  lines.push('"""BitAssay report analysis script — auto-generated."""')
  lines.push('')
  lines.push('import numpy as np')
  lines.push('import matplotlib.pyplot as plt')
  lines.push('')
  lines.push(`# Report metadata`)
  lines.push(`# Generated: ${report.timestamp}`)
  lines.push(`# Input: ${report.inputFile.name} (${report.inputFile.sampleCount} samples)`)
  lines.push('')

  // SP 800-22
  if (report.results.sp80022) {
    lines.push('# SP 800-22 Results')
    lines.push('sp80022_names = [')
    for (const t of report.results.sp80022.tests) {
      lines.push(`    "${t.testName}",`)
    }
    lines.push(']')
    lines.push(`sp80022_pvalues = np.array([${report.results.sp80022.tests.map((t) => t.pValues[0] ?? 0).join(', ')}])`)
    lines.push(`sp80022_passed = np.array([${report.results.sp80022.tests.map((t) => t.overallPassed ? 'True' : 'False').join(', ')}])`)
    lines.push('')
    lines.push('# Plot SP 800-22 p-values')
    lines.push('fig, ax = plt.subplots(figsize=(12, 5))')
    lines.push('colors = ["green" if p else "red" for p in sp80022_passed]')
    lines.push('ax.barh(sp80022_names, sp80022_pvalues, color=colors, alpha=0.7)')
    lines.push('ax.axvline(x=0.01, color="red", linestyle="--", label="alpha=0.01")')
    lines.push('ax.set_xlabel("p-value")')
    lines.push('ax.set_title("SP 800-22 Test Results")')
    lines.push('ax.legend()')
    lines.push('plt.tight_layout()')
    lines.push('plt.savefig("sp80022_results.png", dpi=150)')
    lines.push('plt.show()')
    lines.push('')
  }

  // SP 800-90B
  if (report.results.sp80090b) {
    const r = report.results.sp80090b
    lines.push('# SP 800-90B Results')
    lines.push(`sp90b_min_entropy = ${r.finalMinEntropy}`)
    lines.push(`sp90b_max_entropy = ${r.theoreticalMaxEntropy}`)
    lines.push(`sp90b_efficiency = ${r.entropyEfficiency}`)
    lines.push(`sp90b_iid = ${r.iidTestResults.iidConclusion ? 'True' : 'False'}`)
    lines.push('sp90b_estimators = {')
    for (const est of r.entropyEstimates) {
      lines.push(`    "${est.estimatorName}": ${est.minEntropy},`)
    }
    lines.push('}')
    lines.push('')
    lines.push('# Plot entropy estimates')
    lines.push('fig, ax = plt.subplots(figsize=(10, 5))')
    lines.push('ax.barh(list(sp90b_estimators.keys()), list(sp90b_estimators.values()), alpha=0.7)')
    lines.push('ax.axvline(x=sp90b_min_entropy, color="red", linestyle="--", label=f"Min: {sp90b_min_entropy:.4f}")')
    lines.push('ax.set_xlabel("Min-Entropy (bits/symbol)")')
    lines.push('ax.set_title("SP 800-90B Entropy Estimates")')
    lines.push('ax.legend()')
    lines.push('plt.tight_layout()')
    lines.push('plt.savefig("sp90b_results.png", dpi=150)')
    lines.push('plt.show()')
    lines.push('')
  }

  // Gaussian
  if (report.results.gaussian) {
    lines.push('# Gaussian Results')
    lines.push(`gaussian_mu = ${report.results.gaussian.estimatedMu}`)
    lines.push(`gaussian_sigma = ${report.results.gaussian.estimatedSigma}`)
    lines.push('gaussian_tests = {')
    for (const t of report.results.gaussian.distributionTests) {
      lines.push(`    "${t.testName}": {"pValue": ${t.pValue}, "passed": ${t.passed ? 'True' : 'False'}},`)
    }
    lines.push('}')
    lines.push('')
  }

  lines.push('print("BitAssay analysis script complete.")')
  return lines.join('\n')
}

/**
 * Generate a formatted HTML compliance report.
 *
 * Includes executive summary, test-by-test results, parameters,
 * and metadata. Designed for printing to PDF via browser Print dialog.
 */
export function generateHTMLReport(report: ExportReport): string {
  const h = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const passStyle = 'color:#16a34a;font-weight:bold'
  const failStyle = 'color:#dc2626;font-weight:bold'

  const sections: string[] = []

  // Header
  sections.push(`
    <h1 style="border-bottom:2px solid #333;padding-bottom:8px">BitAssay Statistical Test Report</h1>
    <table style="margin-bottom:24px">
      <tr><td style="padding-right:16px;color:#666">Generated</td><td>${new Date().toISOString()}</td></tr>
      <tr><td style="padding-right:16px;color:#666">Tool</td><td>BitAssay — Statistical Test Suite for Physical RNGs</td></tr>
      <tr><td style="padding-right:16px;color:#666">URL</td><td>https://bitassay.zhature.com</td></tr>
    </table>
  `)

  // Executive summary
  const summaryItems: string[] = []
  if (report.results.sp80022) {
    const r = report.results.sp80022
    const badge = r.overallPassed ? `<span style="${passStyle}">PASS</span>` : `<span style="${failStyle}">FAIL</span>`
    summaryItems.push(`<li><strong>SP 800-22:</strong> ${r.passCount}/${r.totalTests} tests passed — ${badge}</li>`)
  }
  if (report.results.sp80090b) {
    const r = report.results.sp80090b
    const badge = r.iidTestResults.iidConclusion ? `<span style="${passStyle}">IID</span>` : `<span style="${failStyle}">Non-IID</span>`
    summaryItems.push(`<li><strong>SP 800-90B:</strong> ${badge}, Min-Entropy = ${r.finalMinEntropy.toFixed(4)} bits/symbol</li>`)
  }
  if (report.results.tunableBias) {
    const r = report.results.tunableBias
    const passCount = r.tests.filter((t) => {
      if ('passed' in t) return (t as { passed?: boolean }).passed !== false
      if ('homogeneity' in t) return (t as { homogeneity: { passed: boolean }; trend: { passed: boolean } }).homogeneity.passed && (t as { homogeneity: { passed: boolean }; trend: { passed: boolean } }).trend.passed
      return true
    }).length
    summaryItems.push(`<li><strong>Tunable-Bias:</strong> ${passCount}/${r.tests.length} tests passed</li>`)
  }
  if (report.results.gaussian) {
    const r = report.results.gaussian
    const passCount = r.distributionTests.filter((t: { passed: boolean }) => t.passed).length
    const total = r.distributionTests.length
    const badge = passCount === total ? `<span style="${passStyle}">Gaussian</span>` : `<span style="${failStyle}">Non-Gaussian</span>`
    summaryItems.push(`<li><strong>Gaussian:</strong> ${passCount}/${total} tests passed — ${badge}</li>`)
  }

  if (summaryItems.length > 0) {
    sections.push(`<h2>Executive Summary</h2><ul>${summaryItems.join('')}</ul>`)
  }

  // SP 800-22 detailed results
  if (report.results.sp80022) {
    const r = report.results.sp80022
    sections.push(`
      <h2>NIST SP 800-22 Statistical Test Suite</h2>
      <p>Significance level &alpha; = ${(report as ExportReport & { parameters?: { alpha?: number } }).parameters?.alpha ?? 0.01}</p>
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f3f4f6;text-align:left">
            <th style="padding:6px 8px;border:1px solid #d1d5db">#</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">Test Name</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">Statistic</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">p-value</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">Result</th>
          </tr>
        </thead>
        <tbody>
          ${r.tests.map((t, i) => `
            <tr>
              <td style="padding:4px 8px;border:1px solid #e5e7eb">${i + 1}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb">${h(t.testName)}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;font-family:monospace">${formatNumber(t.statistic)}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;font-family:monospace">${t.pValues.length > 0 ? formatNumber(t.pValues[0]) : '---'}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;${t.overallPassed ? passStyle : failStyle}">${t.overallPassed ? 'PASS' : 'FAIL'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `)
  }

  // SP 800-90B detailed results
  if (report.results.sp80090b) {
    const r = report.results.sp80090b
    sections.push(`
      <h2>NIST SP 800-90B Entropy Estimation</h2>
      <p><strong>IID Hypothesis:</strong> <span style="${r.iidTestResults.iidConclusion ? passStyle : failStyle}">${r.iidTestResults.iidConclusion ? 'IID (Independent and Identically Distributed)' : 'Non-IID'}</span></p>
      <p><strong>Final Min-Entropy:</strong> ${r.finalMinEntropy.toFixed(6)} bits/symbol</p>
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f3f4f6;text-align:left">
            <th style="padding:6px 8px;border:1px solid #d1d5db">Estimator</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">Min-Entropy (bits)</th>
          </tr>
        </thead>
        <tbody>
          ${r.entropyEstimates.map(est => `
            <tr>
              <td style="padding:4px 8px;border:1px solid #e5e7eb">${h(est.estimatorName)}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;font-family:monospace">${est.minEntropy.toFixed(6)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `)
  }

  // Gaussian detailed results
  if (report.results.gaussian) {
    const r = report.results.gaussian
    sections.push(`
      <h2>Gaussian Distribution Validation</h2>
      <p>Estimated parameters: &mu; = ${r.estimatedMu.toFixed(6)}, &sigma; = ${r.estimatedSigma.toFixed(6)}</p>
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#f3f4f6;text-align:left">
            <th style="padding:6px 8px;border:1px solid #d1d5db">Test</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">p-value</th>
            <th style="padding:6px 8px;border:1px solid #d1d5db">Result</th>
          </tr>
        </thead>
        <tbody>
          ${r.distributionTests.map(t => `
            <tr>
              <td style="padding:4px 8px;border:1px solid #e5e7eb">${h(t.testName)}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;font-family:monospace">${t.pValue.toFixed(6)}</td>
              <td style="padding:4px 8px;border:1px solid #e5e7eb;${t.passed ? passStyle : failStyle}">${t.passed ? 'PASS' : 'FAIL'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `)
  }

  // Footer
  sections.push(`
    <hr style="margin-top:32px">
    <p style="font-size:11px;color:#666">
      This report was generated by BitAssay (https://bitassay.zhature.com).
      For citation, see the BibTeX entry on the home page.
      Report may be printed to PDF via your browser's Print function (Ctrl+P / Cmd+P).
    </p>
  `)

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>BitAssay Compliance Report</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.6; }
    h1 { font-size: 24px; }
    h2 { font-size: 18px; margin-top: 32px; color: #333; }
    table { font-size: 13px; }
    @media print { body { margin: 0; max-width: 100%; } }
  </style>
</head>
<body>
${sections.join('\n')}
</body>
</html>`
}

/**
 * Trigger a file download in the browser.
 */
export function downloadFile(content: string, fileName: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
